/**
 * Backend users API — me hooks. (The by-id GET /v1/users/{id} endpoint and
 * its useUser hook were killed 2026-07-09 — wiring-map ruling, zero callers.)
 *
 * useMe() returns a synthetic UserView from the JWT claims when the
 * Cosmos user doc doesn't exist yet (first login pre-onboarding) —
 * `profileComplete: false` lets the UI nudge to onboarding.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from './client';

/**
 * Canonical role union — re-exported by the api barrel (`lib/api/index.ts`),
 * so this (not the structurally-identical copy in `onboard.ts`) is the type
 * every layout/role-gate consumes. Keep the two in lockstep.
 *
 * AUDITOR-02 (2026-06-10): `external-auditor` is the read-only persona the
 * firm-admin external-auditor grant provisions (`admin-external-auditors-create.ts`
 * stamps `role: 'external-auditor'` on the UserDoc; `accept-invite.ts` promotes
 * it verbatim to `app_metadata.role`, which `deriveRole` reads). It sits in ZERO
 * backend write role-sets — the client routes it to the read-only `/audit`
 * surface and keeps it off `/app` + `/admin`. `trustee`/`beneficiary` (roster
 * display labels) and `counsel`/`witness` (`popia_role` claims) are NOT stamped
 * as a top-level role, so they are intentionally absent here.
 */
export type UserRole =
  | 'user'
  | 'admin'
  | 'rm'
  | 'compliance'
  | 'director'
  | 'super_admin'
  | 'external-auditor';

export interface UserView {
  /** The caller's STABLE auth identity — the Supabase sub — on BOTH server
   *  branches (real UserDoc and the pre-onboarding synthesized view). Safe to
   *  use in identity-comparison payloads (e.g. the LoW acknowledge body,
   *  which the backend gates on `userId === claims.sub`). Grader R2 row 7:
   *  this was the Cosmos doc UUID post-onboarding, 403-ing every owner. */
  id: string;
  /** The Cosmos UserDoc id (doc-CRUD identity). Absent pre-onboarding. */
  docId?: string;
  email: string;
  displayName: string | null;
  role: UserRole;
  trustIds: string[];
  /** Portal access tier for a `user`-role member — trustee or beneficiary. The
   *  two share ONE role and are entirely different jobs, so the invited-arrival
   *  tutorial (`lib/invited-tutorial.ts`) needs it to say the right thing.
   *  ABSENT for staff, and absent on `user` docs written before team-invite
   *  carried a tier — treat absence as 'trustee', matching the trust-side
   *  membership default. */
  tier?: 'trustee' | 'beneficiary';
  /** Optional — Cosmos doc may not exist yet on first login. */
  supabaseUserId?: string;
  tenantId?: string;
  createdAt?: string;
  updatedAt?: string;
  /** F-003 — ISO timestamp of the server-recorded onboarding identity
   *  self-attestation (set via `useConfirmIdentity()` / PATCH /v1/users/me),
   *  or absent if the caller has not confirmed yet. */
  identityConfirmedAt?: string;
  /** Grader R1 rows 6+8 — the caller's OWN persisted FICA identity fields
   *  (self read: the owner seeing their own data back). Absent until the
   *  onboarding identity confirm persists them. */
  idNumber?: string;
  dateOfBirth?: string;
  residentialAddress?: string;
}

export interface MeResponse {
  user: UserView;
  profileComplete: boolean;
  correlationId: string;
}

export function useMe() {
  return useQuery<MeResponse>({
    queryKey: ['users', 'me'],
    queryFn: ({ signal }) => api<MeResponse>('/api/v1/users/me', { signal }),
  });
}
